/* WakeUpSOSModal — Emergency wakefulness rescue.
 * Big buttons, low cognitive load. Timed sequence of 6 steps.
 * The user is FOGGY when they open this — no decisions, just press "Next". */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Zap, ArrowRight, Coffee, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    id: 'breathe',
    emoji: '🌬️',
    title: 'Andas djupt — 4-7-8',
    bg: 'from-[#A2D2FF] to-[#E8F4FF]',
    duration: 45,
    instructions: [
      'In genom näsan i 4 sekunder',
      'Håll andan i 7 sekunder',
      'Ut genom munnen i 8 sekunder',
      'Upprepa 3 gånger',
    ],
    coach: 'Andas långsamt. Syre väcker hjärnan. Du klarar det 💖',
  },
  {
    id: 'cold',
    emoji: '💧',
    title: 'Kallt vatten i ansiktet + handlederna',
    bg: 'from-[#A2D2FF] to-[#B9FBC0]',
    duration: 60,
    instructions: [
      'Gå till diskbänken eller badrummet',
      'Skölj ansiktet med iskallt vatten',
      'Håll handlederna under kranen 20 sek',
      'Kylan väcker pulsen direkt',
    ],
    coach: 'Stå upp. Rör på dig. Jag väntar 🐾',
  },
  {
    id: 'jacks',
    emoji: '🏃',
    title: '30 jumping jacks',
    bg: 'from-[#FFC8DD] to-[#FFE8A3]',
    duration: 30,
    instructions: [
      '30 jumping jacks (eller 10 armhävningar)',
      'Räcker inte plats? Marschera kraftigt på stället',
      'Pulsen UPP = blod till hjärnan',
      'Dimman lättar inom 60 sek',
    ],
    coach: 'Bara 30. Du har gjort svårare saker. Du fixar det! 💪',
  },
  {
    id: 'eyes',
    emoji: '👀',
    title: 'Ögonpaus — 20-20-20',
    bg: 'from-[#B9FBC0] to-[#E0F4E1]',
    duration: 30,
    instructions: [
      'Titta ut genom fönstret',
      'Fokusera på något LÅNGT bort (>6 m)',
      'Stå still och låt ögonen vila',
      'Det här släpper "klippande ögon"',
    ],
    coach: 'Blink långsamt. Låt ögonen vila. 🌸',
  },
  {
    id: 'reset',
    emoji: '🎵',
    title: 'Byt scen + sätt på musik',
    bg: 'from-[#CDB4DB] to-[#FFE8F5]',
    duration: 60,
    instructions: [
      'Sätt på en låt som väcker dig (gärna hög + snabb)',
      'Eller byt uppgift helt — jobba med något annat 5 min',
      'Mikropaus i ett ANNAT rum om möjligt',
      'Hjärnan älskar variation',
    ],
    coach: 'Variation = vakenhet. Du gör det jättebra 💗',
  },
];

function Step({ step, idx, total, onNext, onSkip }) {
  const [secondsLeft, setSecondsLeft] = useState(step.duration);
  const timerRef = useRef(null);

  useEffect(() => {
    setSecondsLeft(step.duration);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [step]);

  const pct = ((step.duration - secondsLeft) / step.duration) * 100;
  const isDone = secondsLeft === 0;

  return (
    <div className={`bg-gradient-to-br ${step.bg} rounded-3xl p-6 sm:p-8 text-center space-y-4`} data-testid={`sos-step-${step.id}`}>
      <p className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-60">
        Steg {idx + 1} av {total}
      </p>
      <div className="text-6xl sm:text-7xl" aria-hidden>{step.emoji}</div>
      <h2 className="font-['Fredoka'] font-bold text-xl sm:text-2xl leading-tight">{step.title}</h2>

      {/* Big timer */}
      <div className="relative w-32 h-32 mx-auto" data-testid={`sos-timer-${step.id}`}>
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="8" />
          <circle cx="50" cy="50" r="44" fill="none" stroke="#D06892" strokeWidth="8"
            strokeDasharray={`${2 * Math.PI * 44}`}
            strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-['Fredoka'] font-bold text-3xl">
          {secondsLeft}s
        </div>
      </div>

      {/* Instructions */}
      <ul className="text-sm space-y-1 text-left max-w-sm mx-auto bg-white/60 rounded-xl p-3" data-testid={`sos-instructions-${step.id}`}>
        {step.instructions.map((line, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-bold">·</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs italic opacity-70 px-2" data-testid={`sos-coach-${step.id}`}>
        🐾 {step.coach}
      </p>

      <div className="flex gap-2">
        <button onClick={onSkip} className="neo-btn flex-1 py-3 bg-white/80 font-bold text-sm" data-testid={`sos-skip-${step.id}`}>
          Hoppa över
        </button>
        <button onClick={onNext} className={`neo-btn flex-[2] py-3 font-bold text-base flex items-center justify-center gap-1 ${isDone ? 'bg-[#B9FBC0] ring-2 ring-green-500 animate-pulse' : 'bg-[#FFC8DD]'}`}
                data-testid={`sos-next-${step.id}`}>
          {isDone ? '✓ Klar — nästa' : 'Klar med detta steg'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============== FINAL FEEDBACK ==============
function FeedbackStep({ onClose, onCoffeeNap, sessionId, stepsCompleted }) {
  const [busy, setBusy] = useState(false);

  const submit = async (outcome) => {
    setBusy(true);
    try {
      if (sessionId) {
        await axios.post(`${API}/wakeup-sos/complete`, {
          session_id: sessionId,
          outcome,
          steps_completed: stepsCompleted,
        }, { withCredentials: true });
      }
      if (outcome === 'better') {
        toast.success('Bra jobbat! +3 poäng för självomsorg 💖');
        onClose();
      } else if (outcome === 'coffee_nap') {
        onCoffeeNap();
      } else if (outcome === 'took_break') {
        toast.success('Klokt val — vila nu. Allt kan vänta 🌸');
        onClose();
      } else {
        toast('Det är okej. Försök igen om en stund 💗');
        onClose();
      }
    } catch { onClose(); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#FFE8F5] to-[#FFFAF0] rounded-3xl p-6 text-center space-y-4" data-testid="sos-feedback">
      <div className="text-6xl">🐾</div>
      <h2 className="font-['Fredoka'] font-bold text-xl">Hur känns det nu?</h2>
      <p className="text-xs opacity-70 max-w-xs mx-auto">
        Ärligt — det är ingen tävling. Pet:en lär sig av dina svar.
      </p>

      <div className="space-y-2">
        <button onClick={() => submit('better')} disabled={busy}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base flex items-center justify-center gap-1"
          data-testid="sos-outcome-better">
          ✨ Mycket bättre — tillbaka till jobb
        </button>
        <button onClick={() => submit('coffee_nap')} disabled={busy}
          className="neo-btn w-full py-3 bg-[#FFE8A3] font-bold text-sm flex items-center justify-center gap-1"
          data-testid="sos-outcome-nap">
          <Coffee className="w-4 h-4" /> Fortfarande dimmig → starta "Kaffe-tupplur" (20 min)
        </button>
        <button onClick={() => submit('took_break')} disabled={busy}
          className="neo-btn w-full py-3 bg-[#A2D2FF] font-bold text-sm"
          data-testid="sos-outcome-break">
          🌙 Jag tar paus / slutar för dagen
        </button>
        <button onClick={() => submit('still_foggy')} disabled={busy}
          className="neo-btn w-full py-2 bg-white/80 text-xs opacity-70"
          data-testid="sos-outcome-still">
          Ingen skillnad — försöker igen sen
        </button>
      </div>
    </div>
  );
}

// ============== COFFEE NAP ==============
function CoffeeNap({ onClose }) {
  const NAP_SECS = 20 * 60;
  const [left, setLeft] = useState(NAP_SECS);
  const [started, setStarted] = useState(false);
  const tRef = useRef(null);

  useEffect(() => {
    if (!started) return;
    tRef.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          clearInterval(tRef.current);
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==');
            audio.play().catch(() => {});
          } catch {/* */}
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tRef.current);
  }, [started]);

  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  const pct = ((NAP_SECS - left) / NAP_SECS) * 100;
  const done = left === 0;

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] to-[#2d1b4d] text-white rounded-3xl p-6 sm:p-8 text-center space-y-4 min-h-[50vh]" data-testid="sos-coffee-nap">
      <p className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-60">Kaffe-tupplur</p>
      <div className="text-6xl">☕😴</div>
      <h2 className="font-['Fredoka'] font-bold text-xl">20 min tupplur efter kaffe</h2>
      {!started ? (
        <>
          <ol className="text-sm space-y-2 text-left max-w-sm mx-auto opacity-90">
            <li><b>1.</b> Drick en kopp kaffe / energidryck NU</li>
            <li><b>2.</b> Lägg dig på soffan eller golvet</li>
            <li><b>3.</b> Slut ögonen — vakna efter 20 min</li>
            <li><b>4.</b> Koffein slår in precis när du vaknar</li>
          </ol>
          <p className="text-xs opacity-60 italic">Bevisat funkar — koffein tar 20 min att verka.</p>
          <button onClick={() => setStarted(true)}
            className="neo-btn w-full py-3 bg-[#FFC8DD] text-black font-bold text-base"
            data-testid="sos-nap-start">
            Starta timer 💤
          </button>
        </>
      ) : (
        <>
          <div className="relative w-48 h-48 mx-auto">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
              <circle cx="50" cy="50" r="44" fill="none" stroke="#FFC8DD" strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 44}`}
                strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-['Fredoka'] font-bold text-4xl">
              {done ? '🌅' : `${mm}:${ss}`}
            </div>
          </div>
          <p className="text-sm opacity-80">{done ? 'Dags att vakna! Sträck på dig och drick vatten 💖' : 'Slut ögonen. Jag väcker dig.'}</p>
        </>
      )}
      <button onClick={onClose} className="neo-btn w-full py-2 bg-white/10 text-white text-sm" data-testid="sos-nap-close">
        {done ? 'Klar' : 'Avbryt'}
      </button>
    </div>
  );
}

// ============== MAIN MODAL ==============
export default function WakeUpSOSModal({ open, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [coffeeNap, setCoffeeNap] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Start session on open
  useEffect(() => {
    if (!open) return;
    setStepIdx(0); setDone(false); setCoffeeNap(false); setSessionId(null);
    axios.post(`${API}/wakeup-sos/start`, {}, { withCredentials: true })
      .then((r) => setSessionId(r.data?.session_id))
      .catch(() => {});
  }, [open]);

  const next = useCallback(() => {
    setStepIdx((i) => {
      if (i + 1 >= STEPS.length) {
        setDone(true);
        return i;
      }
      return i + 1;
    });
  }, []);

  const restart = () => { setStepIdx(0); setDone(false); setCoffeeNap(false); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-md flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
         data-testid="wakeup-sos-modal">
      <div className="w-full max-w-lg my-4 space-y-2">
        <div className="flex items-center justify-between text-white px-2">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-300" />
            <h3 className="font-['Fredoka'] font-bold text-base">Vakna upp-SOS</h3>
          </div>
          <button onClick={onClose} className="bg-white/15 hover:bg-white/25 rounded-full p-2" data-testid="sos-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {coffeeNap ? (
          <CoffeeNap onClose={onClose} />
        ) : done ? (
          <FeedbackStep
            onClose={onClose}
            onCoffeeNap={() => setCoffeeNap(true)}
            sessionId={sessionId}
            stepsCompleted={STEPS.length}
          />
        ) : (
          <Step
            step={STEPS[stepIdx]}
            idx={stepIdx}
            total={STEPS.length}
            onNext={next}
            onSkip={next}
          />
        )}

        {!coffeeNap && !done && (
          <div className="flex justify-center">
            <button onClick={restart} className="text-white/60 hover:text-white text-xs flex items-center gap-1" data-testid="sos-restart">
              <RotateCcw className="w-3 h-3" /> Börja om från steg 1
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
